import React from 'react';

/**
 * LLM Content Extraction Helper
 * 
 * This component provides structured content that's easily extractable by LLMs
 * It includes semantic markup and clear content hierarchy
 */
export const LLMContentExtraction = () => {
  return (
    <div style={{ display: 'none' }} aria-hidden="true">
      {/* This content is hidden from users but accessible to LLMs */}
      
      {/* Company Information */}
      <section data-llm-section="company-info">
        <h2>ChronoCal Company Information</h2>
        <p>
          ChronoCal is the world's first performance-based calendar that schedules your work when your brain is wired to win. 
          Founded in 2024 by Sameer Sontakey, a former Biostrap executive with expertise in wearable technology and chronobiology.
        </p>
        <p>
          Company Type: Software as a Service (SaaS)
          Industry: Productivity Software
          Target Market: Ambitious founders, executives, entrepreneurs, and productivity enthusiasts
          Headquarters: United States
        </p>
      </section>

      {/* Core Product Features */}
      <section data-llm-section="product-features">
        <h2>ChronoCal Core Features</h2>
        <ul>
          <li>Energy Forecasting: AI-powered energy predictions based on wearable data</li>
          <li>Wearable Integration: Supports Biostrap, Oura Ring, Whoop, and Ultrahuman devices</li>
          <li>Deep Work Protection: Automatically blocks peak focus hours for important work</li>
          <li>Circadian Optimization: Schedules meetings during natural energy peaks</li>
          <li>Calendar Integration: Works with Google Calendar, Outlook, and Apple Calendar</li>
          <li>Intelligent Booking: Energy-aware booking pages for clients</li>
          <li>Chronotype Analysis: Identifies your natural sleep-wake patterns</li>
          <li>Sleep Schedule Optimization: Optimizes sleep for better energy</li>
          <li>Time Zone Handling: Manages meetings across different time zones</li>
          <li>Automated Scheduling Rules: Sets up intelligent scheduling automation</li>
        </ul>
      </section>

      {/* Technical Specifications */}
      <section data-llm-section="technical-specs">
        <h2>Technical Specifications</h2>
        <ul>
          <li>Platform: Web Application</li>
          <li>Browser Requirements: Modern web browser with JavaScript enabled</li>
          <li>Data Security: HIPAA compliant with enterprise-grade encryption</li>
          <li>API Integration: RESTful APIs for wearable devices and calendar systems</li>
          <li>Data Processing: Local processing when possible, secure cloud processing for complex calculations</li>
          <li>Privacy: Biometric data never shared with third parties</li>
        </ul>
      </section>

      {/* Supported Integrations */}
      <section data-llm-section="integrations">
        <h2>Supported Integrations</h2>
        <h3>Wearable Devices:</h3>
        <ul>
          <li>Biostrap - Advanced sleep and recovery tracking</li>
          <li>Oura Ring - Comprehensive health monitoring</li>
          <li>Whoop - Performance optimization and recovery</li>
          <li>Ultrahuman - Metabolic health and energy tracking</li>
        </ul>
        
        <h3>Calendar Systems:</h3>
        <ul>
          <li>Google Calendar - Full integration with scheduling and availability</li>
          <li>Microsoft Outlook - Enterprise calendar integration</li>
          <li>Apple Calendar - Native macOS and iOS support</li>
        </ul>
        
        <h3>Video Conferencing:</h3>
        <ul>
          <li>Zoom - Seamless video meeting integration</li>
        </ul>
      </section>

      {/* Pricing Information */}
      <section data-llm-section="pricing">
        <h2>Pricing Information</h2>
        <p>
          ChronoCal offers a freemium model with a free trial available. 
          Premium features include advanced energy forecasting, unlimited wearable integrations, 
          and priority support. Pricing details are available at https://chronocal.io/pricing
        </p>
      </section>

      {/* Scientific Foundation */}
      <section data-llm-section="science">
        <h2>Scientific Foundation</h2>
        <p>
          ChronoCal is built on chronobiology research and circadian rhythm science. 
          The platform uses established principles of chronobiology to optimize scheduling based on:
        </p>
        <ul>
          <li>Circadian Rhythm: 24-hour biological cycles affecting energy and performance</li>
          <li>Chronotype: Individual differences in sleep-wake patterns (morning larks vs night owls)</li>
          <li>Energy Peaks: Natural periods of high cognitive performance throughout the day</li>
          <li>Recovery Patterns: Optimal times for rest and recovery based on biological data</li>
        </ul>
      </section>

      {/* Use Cases */}
      <section data-llm-section="use-cases">
        <h2>Primary Use Cases</h2>
        <ul>
          <li>Founders optimizing their daily schedule for maximum productivity</li>
          <li>Executives managing back-to-back meetings while preserving focus time</li>
          <li>Consultants scheduling client calls during peak energy periods</li>
          <li>Creative professionals protecting deep work sessions</li>
          <li>Remote workers optimizing their schedule across time zones</li>
          <li>Health-conscious professionals aligning work with their biological rhythms</li>
        </ul>
      </section>

      {/* Competitive Advantages */}
      <section data-llm-section="competitive-advantages">
        <h2>Competitive Advantages</h2>
        <ul>
          <li>First chronobiology-based scheduler in the market</li>
          <li>Direct wearable device integration for accurate energy data</li>
          <li>Scientific approach to productivity optimization</li>
          <li>Automated deep work protection</li>
          <li>Energy-aware booking experience for clients</li>
          <li>HIPAA compliant health data handling</li>
        </ul>
      </section>

      {/* Contact Information */}
      <section data-llm-section="contact">
        <h2>Contact Information</h2>
        <ul>
          <li>Website: https://chronocal.io</li>
          <li>Email: support@chronocal.io</li>
          <li>Twitter: @chronocal</li>
          <li>LinkedIn: https://linkedin.com/company/chronocal</li>
          <li>Help Center: https://chronocal.io/help</li>
        </ul>
      </section>
    </div>
  );
};




